export type UserRole = 'ADMIN' | 'OPERATIONS' | 'PARTNER' | 'ORDER_STAFF' | 'TESTER';

export interface AuthPermissionUser {
  role: UserRole;
  canManageOrderCosts?: boolean;
  canManagePurchaseInvoices?: boolean;
  /**
   * Invoice import workflow (purchase/sales drafts, upload, extraction).
   * Backend is a fail-closed Emil/Tomek identity allowlist with no role or
   * ADMIN fallback; the frontend mirror defaults to false for the same reason.
   */
  canManageInvoiceImports?: boolean;
  canManagePurchaseInvoiceImports?: boolean;
  canManageSupplierOrders?: boolean;
  canViewCustomerOrderFallbackCosts?: boolean;
  canEditCustomerOrderFallbackCosts?: boolean;
  canViewCustomerSalePrices?: boolean;
  canEditCustomerSalePrices?: boolean;
  canViewSupplierPurchasePrices?: boolean;
  canEditSupplierPurchasePrices?: boolean;
  /**
   * Catalog average cost of sales („Średni koszt sprzedaży”, product + variant
   * override): edit ADMIN and OPERATIONS; read also the sales rep (2026-09-24).
   */
  canViewProductAverageCost?: boolean;
  canEditProductAverageCost?: boolean;
  /** Planned margin (WP5 plan) of an order: full-margin roles and the sales rep on his own orders. */
  canViewCustomerOrderPlannedMargin?: boolean;
  canEditWarehouseDocumentPurchasePrices?: boolean;
  canEditPZDocumentPurchasePrices?: boolean;
  canViewWZDocumentSalePrices?: boolean;
  canEditWZDocumentSalePrices?: boolean;
  canViewSupplierOrderPurchasePrices?: boolean;
  canEditSupplierOrderPurchasePrices?: boolean;
  canViewSupplierOrderFinance?: boolean;
  canEditSupplierOrderFinance?: boolean;
  canViewSalesDocuments?: boolean;
  canViewPurchaseDocuments?: boolean;
  canViewFinancialReports?: boolean;
  canViewDashboardFullKpis?: boolean;
  canViewCustomerPayments?: boolean;
  canViewTransportCosts?: boolean;
  canEditTransportCosts?: boolean;
  canViewMargin?: boolean;
}

export type BooleanCapabilityKey = Exclude<keyof AuthPermissionUser, 'role'>;
export type RoleInput = string | string[];

export const isAdminUser = (user: AuthPermissionUser | null | undefined) => user?.role === 'ADMIN';

const readCapability = (
  user: AuthPermissionUser | null | undefined,
  key: BooleanCapabilityKey
): boolean | undefined => {
  const value = user?.[key];
  return typeof value === 'boolean' ? value : undefined;
};

export const resolveUserCapability = (
  user: AuthPermissionUser | null | undefined,
  key: BooleanCapabilityKey,
  fallback: boolean
): boolean => readCapability(user, key) ?? fallback;

export const hasUserRole = (user: AuthPermissionUser | null | undefined, role: RoleInput): boolean => {
  if (!user) return false;
  const roles = Array.isArray(role) ? role : [role];
  return roles.includes(user.role);
};
